/**
 * Duplicate Review Queue
 * Review and merge potential duplicate leads
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  GitMerge,
  X as XIcon,
  CheckCircle2,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DuplicateReviewQueue = ({ user, onNavigate }) => {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${user.token}`,
    'Content-Type': 'application/json'
  }), [user.token]);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/phase9/leads/duplicates/queue`, {
        headers: authHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setQueue(data.queue || []);
      } else {
        toast.error('Failed to fetch duplicate queue');
      }
    } catch (error) {
      toast.error('Error fetching queue');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const handleMerge = async (primaryId, duplicateId) => {
    setProcessing(`${primaryId}-${duplicateId}`);
    try {
      const response = await fetch(`${API}/phase9/leads/duplicates/merge`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ data: { primary_id: primaryId, duplicate_id: duplicateId } })
      });

      if (response.ok) {
        toast.success('Leads merged successfully');
        await fetchQueue();
      } else {
        toast.error('Failed to merge leads');
      }
    } catch (error) {
      toast.error('Error merging leads');
    } finally {
      setProcessing(null);
    }
  };

  const handleIgnore = async (leadId, duplicateId) => {
    setProcessing(`${leadId}-${duplicateId}`);
    try {
      const response = await fetch(`${API}/phase9/leads/${leadId}/duplicates/dismiss`, {
        method: 'POST',
        headers: authHeaders()
      });

      if (response.ok) {
        setQueue(queue.filter(item => {
          if (item.lead.id === leadId && item.potential_duplicates[0]?.lead?.id === duplicateId) {
            return false;
          }
          return true;
        }));
        toast.success('Marked as not a duplicate');
      } else {
        toast.error('Failed to dismiss duplicate');
      }
    } catch (error) {
      toast.error('Error dismissing duplicate');
    } finally {
      setProcessing(null);
    }
  };

  const getConfidenceBadgeClass = (confidence) => {
    if (confidence >= 90) return 'bg-red-500/20 text-red-300 border-red-500/30';
    if (confidence >= 75) return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
    return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
  };

  const CompareField = ({ label, primary, duplicate }) => {
    const isDifferent = primary !== duplicate;
    return (
      <div className="grid grid-cols-2 gap-4 py-2 border-b border-white/5">
        <div>
          <p className="text-xs text-white/60 mb-1">{label}</p>
          <p className={`text-sm ${isDifferent ? 'text-white/90 font-medium' : 'text-white/70'}`}>
            {primary || '—'}
          </p>
        </div>
        <div>
          <p className="text-xs text-white/60 mb-1">{label}</p>
          <p className={`text-sm ${isDifferent ? 'text-white/90 font-medium' : 'text-white/70'}`}>
            {duplicate || '—'}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="page-stack" data-testid="duplicate-review-queue">
      {/* Header */}
      <section className="hero-panel ceo-hero glass-panel noise-overlay">
        <div>
          <p className="eyebrow">Phase 9B — Deduplication Engine</p>
          <h2>Duplicate Review Queue</h2>
          <div className="hero-stats">
            <span>{queue.length} Potential Duplicates</span>
            {queue.filter(q => q.potential_duplicates[0]?.confidence >= 90).length > 0 && (
              <span>{queue.filter(q => q.potential_duplicates[0]?.confidence >= 90).length} High Confidence</span>
            )}
          </div>
        </div>
        <Button className="ghost-btn" onClick={fetchQueue} data-testid="refresh-queue-btn">
          <RefreshCw size={16} />
          Refresh Queue
        </Button>
      </section>

      {/* Queue */}
      {loading ? (
        <section className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="glass-panel">
              <Skeleton className="h-8 w-1/3 mb-4 bg-white/10" />
              <Skeleton className="h-24 w-full bg-white/10" />
            </div>
          ))}
        </section>
      ) : queue.length === 0 ? (
        <section className="glass-panel text-center py-16">
          <CheckCircle2 className="h-16 w-16 text-[var(--gold)] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white/90 mb-2">No duplicates found</h3>
          <p className="text-white/60">All leads are unique — your database is clean!</p>
        </section>
      ) : (
        <section className="space-y-6">
          {queue.map((item, idx) => {
            const lead = item.lead;
            const duplicate = item.potential_duplicates[0];
            if (!duplicate) return null;

            const confidence = duplicate.confidence;
            const reasons = duplicate.reasons || [];
            const isProcessing = processing === `${lead.id}-${duplicate.lead.id}`;

            return (
              <article key={idx} className="glass-panel" data-testid={`duplicate-item-${idx}`}>
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-orange-400" />
                    <div>
                      <h3 className="text-base font-semibold text-white/90">Potential Duplicate Match</h3>
                      <p className="text-xs text-white/60">Review and merge or ignore</p>
                    </div>
                  </div>
                  <Badge className={`${getConfidenceBadgeClass(confidence)} text-sm px-3 py-1`}>
                    {confidence}% Match
                  </Badge>
                </div>

                {/* Match Reasons */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {reasons.map((reason, ridx) => (
                    <Badge key={ridx} variant="outline" className="text-xs border-white/20 text-white/70">
                      {reason}
                    </Badge>
                  ))}
                </div>

                {/* Comparison Grid */}
                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-semibold text-white/90">Primary Lead</h4>
                      <Badge className="bg-[var(--gold)]/20 text-[var(--gold)] text-xs">Keep This</Badge>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-semibold text-white/90">Duplicate Lead</h4>
                      <Badge className="bg-red-500/20 text-red-300 text-xs">Will Be Merged</Badge>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <CompareField label="Clinic Name" primary={lead.clinic_name} duplicate={duplicate.lead.clinic_name} />
                  <CompareField label="Email" primary={lead.email} duplicate={duplicate.lead.email} />
                  <CompareField label="Phone" primary={lead.phone} duplicate={duplicate.lead.phone} />
                  <CompareField label="Location" primary={`${lead.city}, ${lead.state}`} duplicate={`${duplicate.lead.city}, ${duplicate.lead.state}`} />
                  <CompareField label="Website" primary={lead.website} duplicate={duplicate.lead.website} />
                </div>

                {/* Actions */}
                <div className="flex gap-4 mt-6 pt-6 border-t border-white/10">
                  <Button
                    className="primary-btn"
                    onClick={() => handleMerge(lead.id, duplicate.lead.id)}
                    disabled={isProcessing}
                    data-testid={`merge-btn-${idx}`}
                  >
                    <GitMerge size={16} />
                    {isProcessing ? 'Merging...' : 'Merge Leads'}
                  </Button>
                  <Button
                    className="ghost-btn"
                    onClick={() => handleIgnore(lead.id, duplicate.lead.id)}
                    disabled={isProcessing}
                    data-testid={`ignore-btn-${idx}`}
                  >
                    <XIcon size={16} />
                    Not a Duplicate
                  </Button>
                </div>
              </article>
            );
          })}
        </section>
      )}
    </div>
  );
};

export default DuplicateReviewQueue;
